<template>
    <div class="policy-wrapper p-4 md:p-6 surface-card border-round-xl shadow-1 text-left">
        <div class="mb-5">
            <p class="text-900 mb-2">Version: 1.0</p>
            <p class="text-600 italic font-light">
                These Terms take effect from the date you access, browse, register an account on, or otherwise use the Platform or its services.
            </p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">1. INTRODUCTION</h2>
            <p class="text-body mb-2">1.1 ILearn’d Tutors (“ILearn’d Tutors”, “we”, “us”, or “the Platform”) is committed to protecting the privacy and personal data of all users, including students, parents or legal guardians, tutors, educational institutes, and general users.</p>
            <p class="text-body mb-2">1.2 This Privacy Policy explains how personal data is collected, used, stored, disclosed, and protected when users access or use the Platform, including the website, mobile application, AI-enabled features, and related services.</p>
            <p class="text-body">1.3 This Policy is issued in accordance with the <b>Personal Data Protection Act, No. 9 of 2022 of Sri Lanka (“PDPA”)</b> and shall be read together with the User Agreement, Cookie Policy, Child Protection & Safeguarding Policy, and AI Usage, Ethics & Governance Policy.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">2. DATA CONTROLLER</h2>
            <p class="text-body mb-2">2.1 ILearn’d Tutors (Sri Lanka) acts as the <b>data controller</b> in respect of personal data processed through the Platform.</p>
            <p class="text-body mb-1">2.2 For data protection matters, users may contact:</p>
            <p class="text-body ml-3 mb-0"><b>ILearn’d Tutors – Privacy & Data Protection Team</b></p>
            <p class="text-body ml-3">📧 Email: <b>support@ilearndsl.com</b></p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">3. SCOPE OF THIS POLICY</h2>
            <p class="text-body mb-2">3.1 This Policy applies to:</p>
            <p class="text-body ml-3 mb-1">(a) students;</p>
            <p class="text-body ml-3 mb-1">(b) parents or legal guardians;</p>
            <p class="text-body ml-3 mb-1">(c) tutors;</p>
            <p class="text-body ml-3 mb-1">(d) educational institutes;</p>
            <p class="text-body ml-3 mb-1">(e) general users;</p>
            <p class="text-body ml-3 mb-1">(f) visitors to the Platform; and</p>
            <p class="text-body ml-3 mb-3">(g) users of AI-enabled features.</p>
            
            <p class="text-body mb-2">3.2 It applies to data collected through:</p>
            <p class="text-body ml-3 mb-1">(a) registration and user profiles;</p>
            <p class="text-body ml-3 mb-1">(b) tutoring sessions and messaging;</p>
            <p class="text-body ml-3 mb-1">(c) payments and subscriptions;</p>
            <p class="text-body ml-3 mb-1">(d) AI-powered tools and analytics;</p>
            <p class="text-body ml-3 mb-1">(e) customer support interactions; and</p>
            <p class="text-body ml-3">(f) safety, safeguarding, and SOS systems.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">4. LEGAL BASIS FOR PROCESSING</h2>
            <p class="text-body mb-2">4.1 Personal data is processed on one or more of the following lawful bases under the PDPA:</p>
            <p class="text-body ml-3 mb-1">(a) explicit user consent;</p>
            <p class="text-body ml-3 mb-1">(b) performance of a contract (Platform services);</p>
            <p class="text-body ml-3 mb-1">(c) compliance with legal obligations;</p>
            <p class="text-body ml-3 mb-1">(d) protection of vital interests (particularly of children); and</p>
            <p class="text-body ml-3">(e) legitimate interests, including platform security, fraud prevention, and service improvement.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">5. CATEGORIES OF PERSONAL DATA COLLECTED</h2>
            <p class="text-body font-bold mb-1">5.1 Identity & Contact Information</p>
            <p class="text-body mb-3">Name, NIC or passport number (where applicable), date of birth, email address, phone number, and address.</p>

            <p class="text-body font-bold mb-1">5.2 Account & Profile Information</p>
            <p class="text-body mb-3">Usernames, academic level or subjects, tutor qualifications, institute details, profile images, availability, and preferences.</p>

            <p class="text-body font-bold mb-1">5.3 Student & Academic Data</p>
            <p class="text-body mb-3">Grade level, subjects, session history, uploaded materials, learning preferences, and performance indicators (where enabled).</p>

            <p class="text-body font-bold mb-1">5.4 Communication Data</p>
            <p class="text-body mb-1">Messages exchanged on the Platform, session metadata, support tickets, feedback, and complaints.</p>
            <p class="text-body mb-3 font-semibold text-red-600">Off-platform communication with minors is strictly prohibited.</p>

            <p class="text-body font-bold mb-1">5.5 AI-Generated and Processed Data</p>
            <p class="text-body mb-3">AI summaries, notes, transcriptions (where enabled), learning insights, and safety-related behavioural flags.</p>

            <p class="text-body font-bold mb-1">5.6 Payment & Financial Data</p>
            <p class="text-body mb-1">Transaction references, amounts, subscription status, and refund records.</p>
            <p class="text-body mb-3">ILearn’d Tutors does not store full card details; payments are handled by secure third-party gateways.</p>

            <p class="text-body font-bold mb-1">5.7 Technical & Usage Data</p>
            <p class="text-body">IP address, device type, operating system, browser/app version, log files, cookies, and analytics data.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">6. DATA RELATING TO CHILDREN</h2>
            <p class="text-body mb-2">6.1 Where users are under eighteen (18) years of age:</p>
            <p class="text-body ml-3 mb-1">(a) <b>verifiable parental or legal guardian consent</b> is mandatory;</p>
            <p class="text-body ml-3 mb-1">(b) enhanced safeguards and monitoring apply; and</p>
            <p class="text-body ml-3 mb-3">(c) data is processed strictly for educational delivery and safety.</p>
            <p class="text-body mb-2">6.2 ILearn’d Tutors does not knowingly collect unnecessary or excessive data relating to children.</p>
            <p class="text-body">6.3 Any suspected abuse, exploitation, or safeguarding risk is escalated in accordance with the <b>Child Protection & Safeguarding Policy.</b></p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">7. PURPOSES OF DATA USE</h2>
            <p class="text-body mb-2">7.1 Personal data is used to:</p>
            <p class="text-body ml-3 mb-1">(a) create and manage user accounts;</p>
            <p class="text-body ml-3 mb-1">(b) facilitate tutoring sessions and scheduling;</p>
            <p class="text-body ml-3 mb-1">(c) process payments and refunds;</p>
            <p class="text-body ml-3 mb-1">(d) enable AI-supported learning tools;</p>
            <p class="text-body ml-3 mb-1">(e) monitor safety and prevent misuse;</p>
            <p class="text-body ml-3 mb-1">(f) enforce Platform policies;</p>
            <p class="text-body ml-3 mb-1">(g) provide customer support;</p>
            <p class="text-body ml-3 mb-1">(h) improve services and user experience; and</p>
            <p class="text-body ml-3">(i) comply with legal and regulatory obligations.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">8. AI AND AUTOMATED PROCESSING</h2>
            <p class="text-body mb-2">8.1 AI systems may process personal data to generate summaries, learning insights, recommendations, and safety flags.</p>
            <p class="text-body mb-2">8.2 AI outputs are <b>assistive only</b> and do not replace tutors or human judgment.</p>
            <p class="text-body">8.3 AI systems do not make final disciplinary, academic, or legal decisions. Human review is applied where required.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">9. DATA SHARING & DISCLOSURE</h2>
            <p class="text-body font-bold mb-1">9.1 Service Providers</p>
            <p class="text-body mb-3">Data may be shared with vetted service providers (payment processors, cloud hosting, analytics, AI services) under strict confidentiality and data protection obligations.</p>
            <p class="text-body font-bold mb-1">9.2 Legal & Safety Authorities</p>
            <p class="text-body">Data may be disclosed to the <b>Sri Lanka Police, National Child Protection Authority (NCPA),</b> courts, or regulators where required by law or necessary to protect users.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">10. INTERNATIONAL DATA TRANSFERS</h2>
            <p class="text-body mb-2">10.1 Where data is processed or stored outside Sri Lanka, ILearn’d Tutors ensures:</p>
            <p class="text-body ml-3 mb-1">(a) adequate data protection safeguards; and</p>
            <p class="text-body ml-3">(b) contractual compliance with PDPA requirements.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">11. DATA RETENTION</h2>
            <p class="text-body mb-2">11.1 Data is retained only for as long as necessary:</p>
            <p class="text-body ml-3 mb-1">(a) account data – while active and thereafter as legally required;</p>
            <p class="text-body ml-3 mb-1">(b) payment data – as required by law;</p>
            <p class="text-body ml-3 mb-1">(c) safeguarding records – up to five (5) years;</p>
            <p class="text-body ml-3 mb-3">(d) AI data – anonymised where possible.</p>
            <p class="text-body">11.2 AI-generated transcripts and session summaries may be temporarily stored for operational purposes and subscription access management. Where applicable, transcripts for non-premium users may be retained for a limited period (for example, up to ten (10) days) before automatic deletion.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">12. USER RIGHTS</h2>
            <p class="text-body mb-2">12.1 Subject to the PDPA, users have the right to:</p>
            <p class="text-body ml-3 mb-1">(a) access personal data;</p>
            <p class="text-body ml-3 mb-1">(b) correct inaccurate data;</p>
            <p class="text-body ml-3 mb-1">(c) request erasure (subject to legal limits);</p>
            <p class="text-body ml-3 mb-1">(d) withdraw consent;</p>
            <p class="text-body ml-3 mb-1">(e) object to certain processing; and</p>
            <p class="text-body ml-3 mb-3">(f) lodge complaints with the relevant authority.</p>
            <p class="text-body">12.2 Requests may be submitted to <b>support@ilearndsl.com</b></p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">13. DATA SECURITY</h2>
            <p class="text-body mb-2">13.1 ILearn’d Tutors implements reasonable technical and organisational measures, including encryption, access controls, and staff confidentiality obligations.</p>
            <p class="text-body">13.2 No system is completely secure; however, reasonable safeguards are continuously reviewed and improved.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">14. DATA BREACH NOTIFICATION</h2>
            <p class="text-body mb-2">14.1 In the event of a personal data breach likely to result in harm, ILearn’d Tutors shall:</p>
            <p class="text-body ml-3 mb-1">(a) assess the breach promptly;</p>
            <p class="text-body ml-3 mb-1">(b) notify affected users where required by law; and</p>
            <p class="text-body ml-3">(c) report the breach to relevant authorities in accordance with the PDPA.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">15. COOKIES AND TRACKING</h2>
            <p class="text-body">15.1 Cookies and similar technologies are used as described in the <b>Cookie Policy</b>, which forms part of this Privacy Policy.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">16. MARKETING COMMUNICATIONS</h2>
            <p class="text-body mb-2">16.1 Marketing communications are sent <b>only with explicit consent.</b></p>
            <p class="text-body">16.2 Users may opt out at any time using the unsubscribe option or by contacting support.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">17. THIRD-PARTY LINKS</h2>
            <p class="text-body">17.1 The Platform may contain links to third-party services. ILearn’d Tutors is not responsible for their privacy practices.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">18. CHANGES TO THIS POLICY</h2>
            <p class="text-body mb-2">18.1 This Privacy Policy may be updated from time to time.</p>
            <p class="text-body">18.2 <b>Material changes</b> shall be notified through the Platform and, where required by law (particularly for minors), shall require <b>explicit consent</b> before taking effect.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">19. GOVERNING LAW</h2>
            <p class="text-body">19.1 This Privacy Policy is governed by the laws of the <b>Democratic Socialist Republic of Sri Lanka.</b></p>
        </div>

        <div class="policy-section">
            <h2 class="H5-SemiBold text-primary mb-3">20. CONTACT INFORMATION</h2>
            <div class="p-3 border-round bg-faded">
                <p class="text-body font-bold mb-1"><b>ILearn’d Tutors – Legal Team</b></p>
                <p class="text-body mb-1">Email: support@ilearndsl.com</p>
                <p class="text-body mb-0">Address: 306/1 Highlevel Road, Colombo 06, Sri Lanka</p>
            </div>
        </div>
    </div>
</template>

<style scoped>
.text-primary { color: #1D97BD !important; }
.bg-faded { background-color: rgba(29, 151, 189, 0.04); }
.text-body {
    font-size: 0.95rem;
    color: #4A5568;
    font-weight: 400;
    line-height: 1.6;
}
.ml-3 { margin-left: 1.5rem; }
.text-red-600 { color: #e53e3e; }
</style>